import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '../stores/authStore';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth: boolean;
    title?: string;
    permissions?: string[];
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Вход в систему',
    },
  },
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Главная',
    },
  },
  {
    path: '/cemetery/:cemetery_id',
    name: 'cemetery',
    component: () => import('@/views/CemeteryView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Информация',
    }
  },
  {
    path: '/map',
    name: 'map',
    component: () => import('@/views/MapView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Карта кладбища', 
    }
  },
  {
    path: '/cemetery/:cemetery_id/settings',
    name: 'cemeterySettings',
    component: () => import('@/views/CemeterySettingsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Настройки кладбища',
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  
  // Установка заголовка страницы
  document.title = to.meta.title 
    ? `${import.meta.env.VITE_APP_NAME || 'ГИС Учет захоронений'} | ${to.meta.title}` 
    : import.meta.env.VITE_APP_NAME || 'ГИС Учет захоронений';

  // Проверяем наличие токена через inspectToken()
  const hasToken = authStore.inspectToken();

  // Если пользователь авторизован (есть токен) и пытается зайти на login - перенаправляем на главную
  if (to.name === 'login' && hasToken) {
    return { name: 'home' };
  }

  // Если маршрут требует авторизации и нет токена
  if (to.meta.requiresAuth && !hasToken) {
    return { 
      name: 'login',
      query: { redirect: to.fullPath } // Сохраняем URL для перенаправления после входа
    };
  }

  // Если есть токен, но нет данных пользователя - загружаем их
  if (hasToken && !authStore.user) {
    try {
      await authStore.refreshToken();
    } catch (error) {
      // Если не удалось загрузить пользователя - разлогиниваем
      authStore.LOGOUT();
      return { name: 'login' };
    }
  }

  return true;
});

export default router;